import React, { useState, useEffect, useMemo } from "react";
import axiosInstance from "../../common/axiosInstance";
import { useOrgUsersStore } from "../../store/store";
import { usePremisesStore } from "../../store/store";
import {
  Mail,
  Users,
  X,
  Search,
  CheckCircle2,
  AlertCircle,
  Loader2,
} from "lucide-react";

interface SendEmailNotificationToUsersProps {
  selectedUsers: string[];
  fireDrillData: {
    premise: string;
    date_of_test_drills: string | Date | null;
    person_responsibility: string;
    area: string;
    floor: string;
    region: string;
    next_drill_scheduled: string | Date | null;
    premisesId?: string;
    moduleType?: string;
  };
  onClose?: () => void;
}

const SendEmailNotificationToUsers: React.FC<
  SendEmailNotificationToUsersProps
> = ({ selectedUsers, fireDrillData, onClose }) => {
  const [emailContent, setEmailContent] = useState({
    subject: "",
    body: "",
  });
  const [recipients, setRecipients] = useState<string[]>([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [isSending, setIsSending] = useState(false);
  const [sendStatus, setSendStatus] = useState<"idle" | "success" | "error">(
    "idle"
  );
  const [errorMessage, setErrorMessage] = useState("");
  const { fetchOrgUsers, orgUsersData } = useOrgUsersStore();
  const { allPremises } = usePremisesStore();

  useEffect(() => {
    setRecipients(selectedUsers);
  }, [selectedUsers]);

  useEffect(() => {
    const moduleType = fireDrillData.moduleType || "fire_safety";
    const isHazardousMaterials = moduleType === "hazardous_materials";

    const formattedDate = fireDrillData.date_of_test_drills
      ? new Date(fireDrillData.date_of_test_drills).toLocaleDateString("en-GB")
      : "Not specified";

    const responsibleUser = orgUsersData.find((user: any) => {
      const userId =
        typeof user._id === "object" && "$oid" in user._id
          ? user._id.$oid
          : String(user._id);
      return userId === fireDrillData.person_responsibility;
    });

    const responsiblePersonName = responsibleUser
      ? `${responsibleUser.firstName} ${responsibleUser.lastName}`
      : "Not available";
    const responsibleEmail = responsibleUser?.email || "Not available";

    // Get premise address from premises store
    const premiseInfo = allPremises.find(
      (premise: any) =>
        premise._id === fireDrillData.premisesId ||
        premise.premisesName === fireDrillData.premise
    );

    const premiseAddress = premiseInfo?.addressLine1 || "N/A";

    const drillType = isHazardousMaterials
      ? "hazardous materials safety drill"
      : "fire drill";

    const teamName = isHazardousMaterials
      ? "Hazardous Materials Management Team"
      : "Fire Drill Management Team";

    setEmailContent({
      subject: `${
        isHazardousMaterials ? "Hazardous Materials" : "Fire"
      } Drill Notification - ${fireDrillData.premise} on ${formattedDate}`,
      body: `
        You have been selected to participate in an upcoming ${drillType}:
    
        ${isHazardousMaterials ? "Hazardous Materials" : "Fire"} Drill Details:
        - Region: ${fireDrillData.region || "N/A"}
        - Premise: ${fireDrillData.premise || "N/A"}
        - Premise Address: ${premiseAddress}
        - Area: ${fireDrillData.area}
        - Floor: ${fireDrillData.floor}
        - Date of Test/Drill: ${formattedDate}
        - Responsible Person: ${responsiblePersonName}
        - Responsible Person Email: ${responsibleEmail}
    
        Please ensure your availability and prepare accordingly. Your participation is important for the safety of all occupants.
    
        If you have any questions or concerns, please contact the responsible person at ${responsibleEmail}.
    
        Thank you,
        ${teamName}`.trim(),
    });
  }, [fireDrillData, orgUsersData, allPremises]);

  const handleRemoveRecipient = (email: string) => {
    setRecipients((prev) => prev.filter((r) => r !== email));
  };

  const filteredRecipients = useMemo(() => {
    return recipients.filter((email) =>
      email.toLowerCase().includes(searchQuery.toLowerCase())
    );
  }, [recipients, searchQuery]);

  const handleSendEmail = async () => {
    if (recipients.length === 0) {
      setErrorMessage("No users selected for notification");
      setSendStatus("error");
      return;
    }

    setIsSending(true);
    setSendStatus("idle");
    setErrorMessage("");

    try {
      const response = await axiosInstance.post(`/api/email`, {
          to: recipients,
          cc: [],
          bcc: [],
          subject: emailContent.subject,
          body: emailContent.body,
          templateType: "generic",
          additionalVariables: [],
        });

      if (!response.data.success) {
        throw new Error(response.data.error || "Failed to send email");
      }

      setSendStatus("success");
      if (onClose) onClose();
    } catch (error) {
      const errorMsg = `Failed to send email: ${
        error instanceof Error ? error.message : "Unknown error"
      }`;
      setErrorMessage(errorMsg);
      setSendStatus("error");
    } finally {
      setIsSending(false);
    }
  };

  return (
    <div className="p-1">
      {/* Header */}
      <div className="flex items-center gap-3 mb-4">
        <div className="p-1.5 bg-teal-50 rounded-lg text-[#0fa9a6]">
          <Mail size={20} />
        </div>
        <div>
          <h2 className="text-lg font-bold text-gray-800">
            Send{" "}
            {fireDrillData.moduleType === "hazardous_materials"
              ? "Hazardous Materials"
              : "Fire"}{" "}
            Drill Notification
          </h2>
          <p className="text-[11px] text-gray-500">
            Notify selected personnel about the upcoming safety drill
          </p>
        </div>
      </div>

      {/* Subject Input */}
      <div className="mb-3">
        <label className="block text-xs font-semibold text-gray-700 mb-1">
          Subject
        </label>
        <input
          type="text"
          value={emailContent.subject}
          onChange={(e) =>
            setEmailContent({ ...emailContent, subject: e.target.value })
          }
          className="w-full border-2 border-gray-200 rounded-lg p-2 transition-all focus:outline-none focus:border-[#0fa9a6] hover:border-gray-300 text-xs"
          placeholder="Enter email subject"
        />
      </div>

      {/* Message Textarea */}
      <div className="mb-3">
        <label className="block text-xs font-semibold text-gray-700 mb-1">
          Message
        </label>
        <textarea
          value={emailContent.body}
          onChange={(e) =>
            setEmailContent({ ...emailContent, body: e.target.value })
          }
          rows={3}
          className="w-full border-2 border-gray-200 rounded-lg p-2 transition-all focus:outline-none focus:border-[#0fa9a6] hover:border-gray-300 font-mono text-[11px] leading-relaxed"
          placeholder="Enter email message body..."
        />
      </div>

      {/* Recipients Component */}
      <div className="mb-4 border border-gray-200 rounded-lg p-3 bg-gray-50">
        <div className="flex flex-wrap justify-between items-center gap-2 mb-2">
          <div className="flex items-center gap-1.5">
            <Users size={14} className="text-[#0fa9a6]" />
            <span className="text-xs font-bold text-gray-700">
              Selected Recipients ({filteredRecipients.length})
            </span>
            {recipients.length !== selectedUsers.length && (
              <span className="text-[10px] text-gray-400 font-medium">
                ({recipients.length} of {selectedUsers.length} total)
              </span>
            )}
          </div>

          {/* Search bar inside recipients section */}
          {recipients.length > 5 && (
            <div className="relative w-44">
              <Search
                size={12}
                className="absolute left-2 top-2 text-gray-400"
              />
              <input
                type="text"
                placeholder="Search emails..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-6 pr-2 py-0.5 text-[10px] border border-gray-300 rounded-md focus:outline-none focus:border-[#0fa9a6] bg-white"
              />
            </div>
          )}
        </div>

        {filteredRecipients.length > 0 ? (
          <div className="flex flex-wrap gap-1.5 max-h-20 overflow-y-auto p-1.5 bg-white rounded-lg border border-gray-100 shadow-inner">
            {filteredRecipients.map((email) => (
              <div
                key={email}
                className="flex items-center gap-1 pl-2 pr-1 py-0.5 bg-teal-50 border border-teal-100 text-teal-800 rounded-full text-[10px] font-medium hover:bg-teal-100 transition-colors"
              >
                <span className="truncate max-w-[180px]">{email}</span>
                <button
                  type="button"
                  onClick={() => handleRemoveRecipient(email)}
                  className="p-0.5 rounded-full hover:bg-teal-200 text-teal-600 transition-colors focus:outline-none"
                  title="Remove recipient"
                >
                  <X size={8} />
                </button>
              </div>
            ))}
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center py-4 bg-white rounded-lg border border-dashed border-gray-300 text-center">
            <p className="text-[10px] text-gray-500 font-medium">
              {recipients.length === 0
                ? "No recipients selected"
                : "No matching recipients found"}
            </p>
            {recipients.length > 0 && (
              <button
                type="button"
                onClick={() => setSearchQuery("")}
                className="text-[10px] text-[#0fa9a6] font-semibold hover:underline mt-0.5"
              >
                Clear search filter
              </button>
            )}
          </div>
        )}
      </div>

      {/* Status Notifications */}
      {sendStatus === "success" && (
        <div className="mb-4 p-2 bg-green-50 border border-green-200 text-green-800 rounded-lg flex items-center gap-2 text-xs animate-fadeIn">
          <CheckCircle2 size={16} className="text-green-600 flex-shrink-0" />
          <span className="font-medium">Emails sent successfully!</span>
        </div>
      )}

      {sendStatus === "error" && (
        <div className="mb-4 p-2 bg-red-50 border border-red-200 text-red-800 rounded-lg flex items-center gap-2 text-xs animate-fadeIn">
          <AlertCircle size={16} className="text-red-600 flex-shrink-0" />
          <span className="font-medium">{errorMessage}</span>
        </div>
      )}

      {/* Buttons */}
      <div className="flex justify-end gap-2.5 pt-2.5 border-t border-gray-150">
        <button
          type="button"
          onClick={onClose}
          disabled={isSending}
          className="px-4 py-1.5 border border-gray-300 text-gray-700 font-semibold rounded-full hover:bg-gray-50 hover:border-gray-400 transition-all active:scale-95 disabled:opacity-50 text-xs"
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={handleSendEmail}
          disabled={isSending || recipients.length === 0}
          className="px-5 py-1.5 bg-[#0fa9a6] text-white font-semibold rounded-full hover:bg-[#197f7d] transition-all active:scale-95 flex items-center justify-center gap-1.5 disabled:bg-gray-300 disabled:text-gray-500 disabled:cursor-not-allowed text-xs"
        >
          {isSending ? (
            <>
              <Loader2 size={14} className="animate-spin" />
              <span>Sending...</span>
            </>
          ) : (
            <span>Send Notification</span>
          )}
        </button>
      </div>
    </div>
  );
};

export default SendEmailNotificationToUsers;
